import { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Textarea from '@cloudscape-design/components/textarea';
import Select from '@cloudscape-design/components/select';
import { Override, updateOverride } from './api';

interface Props {
  override: Override;
  idToken: string;
  onDismiss: () => void;
  onSaved: () => void;
}

export default function EditModal({ override, idToken, onDismiss, onSaved }: Props) {
  const [addedTags, setAddedTags] = useState(override.addedTags.join(', '));
  const [removedTags, setRemovedTags] = useState(override.removedTags.join(', '));
  const [include, setInclude] = useState(String(override.includeInScheduling));
  const [priority, setPriority] = useState(String(override.priorityScore));
  const [comment, setComment] = useState(override.comment ?? '');
  const [saving, setSaving] = useState(false);

  const parseTags = (val: string) =>
    val
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOverride(idToken, override.testerAlias, {
        addedTags: parseTags(addedTags),
        removedTags: parseTags(removedTags),
        includeInScheduling: include === 'true',
        priorityScore: parseInt(priority, 10) || 50,
        comment: comment.trim(),
      });
      onSaved();
    } catch {
      alert('Failed to save override');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header={`Edit Override — ${override.testerAlias}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label="Added Tags (comma-separated)">
          <Input value={addedTags} onChange={({ detail }) => setAddedTags(detail.value)} />
        </FormField>
        <FormField label="Removed Tags (comma-separated)">
          <Input value={removedTags} onChange={({ detail }) => setRemovedTags(detail.value)} />
        </FormField>
        <FormField label="Include in Scheduling">
          <Select
            selectedOption={{ value: include, label: include === 'true' ? 'Yes' : 'No' }}
            onChange={({ detail }) => setInclude(detail.selectedOption.value ?? 'true')}
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
          />
        </FormField>
        <FormField label="Priority Score (0–100)">
          <Input type="number" value={priority} onChange={({ detail }) => setPriority(detail.value)} />
        </FormField>
        <FormField label="Comment" description="Optional note explaining why this override exists.">
          <Textarea
            value={comment}
            placeholder="e.g. On rotation until Q3; excluded per manager request."
            onChange={({ detail }) => setComment(detail.value)}
          />
        </FormField>
        {override.lastModifiedAt && (
          <Box color="text-status-inactive" fontSize="body-s">
            Last modified {new Date(override.lastModifiedAt).toLocaleString()}
            {override.lastModifiedBy ? ` by ${override.lastModifiedBy}` : ''}
          </Box>
        )}
      </SpaceBetween>
    </Modal>
  );
}
